import React, { useState, useEffect, useMemo } from "react";
import { useForm, Controller, FormProvider, set } from "react-hook-form";
import Input from "../../../components/elements/form/input";
import Icon from "../../../components/icons";
import CommonDropdown from "../../../components/elements/form/commonDropdown";
import { useLookup, getLookupDisplayValue } from "../../../_common/hookFunction";
import { conditionOptions, LOOKUP_VALUE } from "../../../_common/constant";
import SelectBox from "../../../components/elements/form/selectBox";
import Button from "../../../components/elements/button";
import InfiniteScrollTable from "../../../components/InfiniteScrollTable";
import { getInventoryCount, getRetailPrices, getCashPriceDisplay, formatQuantity, getPricingMode, CashDiscountConfig } from "../../../_common/functions";
import { useProductsApi } from "../../../store/api/hooks/productsApiHook";
import { useCashDiscountApi } from "../../../store/api/hooks/cashDiscountApiHook";
import { useLocationsSelector } from "../../../store/selectors/masterSelector";
import { createPositiveWholeNumberHandler, getPositiveWholeNumberInputProps } from "../../../_common/validationUtils";
import { useTranslation } from "react-i18next";
import { useAppLoader } from "../../../_common/pageLoader/appLoaderHook";
import toast from "react-hot-toast";

interface Props {
    onClose: () => void;
    onConfirm: (selectedProducts: any) => void;
    productData?: Array<any> | null;
    category?: any;
    disableCategory?: boolean;
    hideCategory?: boolean;
    defaultStatus?: string; // Add optional prop for default status
    location?: any; // Location passed from parent component
}

type FormValues = {
    keywordSearch: string;
    category: any;
    status: any;
    inventory: any;
    stockCondition: any;
    stockQty: string;
    productType: any;
    location: any;
};

function ProductSearchModalAll({ onClose, onConfirm, productData = [], category = null, disableCategory = false, hideCategory = false, defaultStatus, location = null }: Props) {
    const { t } = useTranslation("discounts");
    const localizedConditionOptions = useMemo(
        () => conditionOptions.map((o) => ({ ...o, label: t(`common:conditions.${o.value}`) })),
        [t]
    );
    const lookup = useLookup();
    const productsApi = useProductsApi();
    const cashDiscountApi = useCashDiscountApi();
    const locations = useLocationsSelector();
    const { showLoader, hideLoader } = useAppLoader();
    const [cashDiscountConfig, setCashDiscountConfig] = useState<CashDiscountConfig>(null);

    useEffect(() => {
        cashDiscountApi.getCashDiscountSettings(
            {},
            (resp: any) => {
                if (resp?.data) {
                    setCashDiscountConfig({
                        isCashDiscount: resp.data.cashDiscountEnabled,
                        cashDiscount: resp.data.cashDiscountPercent,
                        isDualPricing: resp.data.dualPricingEnabled ?? false,
                        dualPriceDefault: resp.data.fkDualPriceDefaultLookupValueID ?? null,
                    });
                }
            },
            () => {}
        );
    }, []);

    // Get status from lookup if defaultStatus prop is provided
    const initialStatus = useMemo(() => {
        if (!defaultStatus) return [];
        const statuses = lookup.getProductStatus();
        const targetLookupId = defaultStatus.toLowerCase() === "active" ? LOOKUP_VALUE.PRODUCT_STATUS_ACTIVE : null;
        const status = targetLookupId != null ? statuses.find((s: any) => s.lookupId === targetLookupId) : undefined;
        return status ? [{ label: getLookupDisplayValue(status, t), value: status.lookupId }] : [];
    }, [defaultStatus, lookup, t]);

    // Default search values
    const defaultSearchValues: FormValues = {
        keywordSearch: "",
        category: category || "",
        status: initialStatus,
        inventory: "",
        stockCondition: "",
        stockQty: "",
        productType: "",
        location: location || "",
    };

    // Modal states
    const [modalProducts, setModalProducts] = useState<any>(null);
    const [modalSelectedProducts, setModalSelectedProducts] = useState<any>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [activeSearchData, setActiveSearchData] = useState<FormValues>(defaultSearchValues);

    // Search form
    const searchMethods = useForm<FormValues>({
        defaultValues: {
            keywordSearch: "",
            category: category || "",
            status: initialStatus,
            inventory: "",
            stockCondition: "",
            stockQty: "",
            productType: "",
            location: location || "",
        },
    });

    const {
        control,
        setValue,
        reset,
        getValues,
        formState: { errors },
    } = searchMethods;

    useEffect(() => {
        if (category) {
            setValue("category", category);
        }

        // Set location from prop
        if (location) {
            setValue("location", location);
        }

        // Set all active statuses by default (startsWith "active" excludes "Inactive")
        const statusLookup = lookup.getProductStatus();
        const activeStatuses = statusLookup
            ?.filter((s: any) => s.lookupId === LOOKUP_VALUE.PRODUCT_STATUS_ACTIVE)
            .map((s: any) => ({ value: s.lookupId.toString(), label: getLookupDisplayValue(s, t) })) || [];

        if (activeStatuses.length > 0) {
            setValue("status", activeStatuses);
        }

        // Create initial search data with active statuses and location
        const initialSearchData = {
            ...defaultSearchValues,
            status: activeStatuses,
            location: location || ""
        };

        // Perform initial search when modal opens
        setActiveSearchData(initialSearchData);
        fetchAllProducts(initialSearchData);
    }, [productData, category]);

    // Build API params for a given page, reusing the same filter mapping as the regular search modal
    const buildSearchParams = (searchData: FormValues, pageNumber: number, pageSize: number) => {
        let params: any = {
            keywordSearch: searchData.keywordSearch || null,
            pageNumber,
            pageSize,
        };

        // Add category filter
        if (searchData.category?.length > 0) {
            params.categoryIds = searchData.category.map((item: any) => atob(item.value));
        } else if (searchData.category?.value) {
            params.categoryIds = [atob(searchData.category.value)];
        }

        // Add status filter
        if (searchData.status?.length > 0) {
            params.status = searchData.status.map((item: any) => item.value);
        }

        // Add inventory filter
        if (searchData.inventory?.length > 0) {
            params.inventoryStatus = searchData.inventory.map((item: any) => item.value.toString());
        }

        // Add stock quantity filter
        if (searchData.stockCondition?.value && searchData.stockQty) {
            params.stockOp = searchData.stockCondition.value;
            params.stockValue = searchData.stockQty;
        }

        // Add product type filter
        if (searchData.productType?.length > 0) {
            params.productTypeIds = searchData.productType.map((item: any) => item.value.toString());
        } else if (searchData.productType?.value) {
            params.productTypeIds = [searchData.productType.value.toString()];
        }

        // Add location filter
        if (searchData.location?.length > 0) {
            params.locationIds = searchData.location.map((item: any) => atob(item.value));
        } else if (searchData.location?.value) {
            params.locationIds = [atob(searchData.location.value)];
        }

        return params;
    };

    // Map raw API items (products or categories) to the product-like shape used by the table
    const mapApiItems = (items: any[]) => {
        return (items || []).map((item: any) => {
            // Check if this is a category response or product response
            if (item.productCategoryId) {
                // This is a category, map it to product-like structure
                return {
                    ...item,
                    id: item.productCategoryId,
                    productId: item.productCategoryId,
                    productName: item.categoryName,
                    categoryName: item.parentCategoryName || item.categoryName,
                    brandName: "", // Categories don't have brands
                    inventoryLevels: [{ quantityInStock: item.qtyInStock || 0 }],
                    prices: [{ retailPriceExclTax: 0 }], // Categories don't have prices
                    productImages: item.images || [],
                    variants: [], // No variants for categories
                };
            } else {
                // This is a product, use existing mapping
                return {
                    ...item,
                    id: item.productId,
                    variants: item.variants || [], // Ensure variants array exists
                };
            }
        });
    };

    const searchProductPage = (params: any): Promise<any> => {
        return new Promise((resolve, reject) => {
            productsApi.searchProduct(params, resolve, reject);
        });
    };

    // Fetch every matching product across all pages (no manual pagination), then select all by default
    const fetchAllProducts = async (searchDataArg?: FormValues) => {
        const searchData = searchDataArg || activeSearchData;
        const MAX_PAGES = 200;
        const PAGE_SIZE = 200;

        showLoader();
        setModalProducts(null);
        setModalSelectedProducts([]);

        let accumulated: any[] = [];
        let pageNumber = 1;
        let totalRowsLocal = 0;

        try {
            while (pageNumber <= MAX_PAGES) {
                const params = buildSearchParams(searchData, pageNumber, PAGE_SIZE);
                const response: any = await searchProductPage(params);

                if (!response?.success || !response?.data) {
                    throw new Error("Product search failed");
                }

                totalRowsLocal = response.data.totalRows || 0;
                const pageItems = mapApiItems(response.data.items);

                if (pageItems.length === 0) {
                    break;
                }

                accumulated = accumulated.concat(pageItems);

                if (accumulated.length >= totalRowsLocal) {
                    break;
                }

                pageNumber += 1;
            }

            setTotalRows(totalRowsLocal);
            setModalProducts(accumulated);

            const stamped = accumulated.map((product: any) => ({
                ...product,
                isActive: true,
                variants: (product.variants || []).map((v: any) => ({ ...v, isActive: true })),
            }));
            setModalSelectedProducts(stamped);
        } catch (error) {
            console.log("Modal Product search error:", error);
            setModalProducts([]);
            setModalSelectedProducts([]);
            setTotalRows(0);
            toast.error(t("productSearchModalAll.toasts.loadFailed"));
        } finally {
            hideLoader();
        }
    };

    const handleSearchClick = () => {
        const currentSearchData = getValues();
        setActiveSearchData(currentSearchData);
        fetchAllProducts(currentSearchData);
    };

    // Get selected location IDs from prop for filtering inventory
    const selectedLocationIds = useMemo(() => {
        if (!location) return [];
        
        // Handle array of locations (multiselect)
        // Keep IDs base64 encoded since API returns them encoded in inventoryLevels
        if (Array.isArray(location) && location.length > 0) {
            return location.map((loc: any) => loc.value);
        }
        
        // Handle single location object
        if (location?.value) {
            return [location.value];
        }
        
        return [];
    }, [location]);

    // Create inventory data for all products - filtered by selected locations
    const inventoryData = useMemo(() => {
        const data: { [key: string]: any } = {};

        if (!modalProducts) return data;

        modalProducts.forEach((product: any) => {
            const getInventoryOptions = (inventoryLevels: any[]) => {
                if (!inventoryLevels || inventoryLevels.length === 0) {
                    return [{ label: t("productSearchModalAll.values.noStockData"), value: "none" }];
                }

                // Group by fkLocationId and sum quantities, filtering by selected locations
                const locationMap = new Map();

                inventoryLevels.forEach((level: any) => {
                    const locationId = level.fkLocationId;
                    
                    // Only include if this location is in the selected locations, or if no locations selected show all
                    if (selectedLocationIds.length === 0 || selectedLocationIds.includes(locationId)) {
                        const quantity = level.quantityInStock || 0;

                        if (locationMap.has(locationId)) {
                            locationMap.set(locationId, locationMap.get(locationId) + quantity);
                        } else {
                            locationMap.set(locationId, quantity);
                        }
                    }
                });

                // If no matching locations found after filtering
                if (locationMap.size === 0) {
                    return [{ label: t("productSearchModalAll.values.noStockData"), value: "none" }];
                }

                // Convert to options array and sort by quantity descending (highest first)
                const options = Array.from(locationMap.entries())
                    .map(([locationId, totalQuantity]) => {
                        // Find location name from locations selector using fkLocationId
                        const location = locations?.find((loc: any) => loc.locationId === locationId);
                        const locationName = location?.locationName || t("productSearchModalAll.values.unknownLocation");

                        return {
                            label: product.trackInventory === false
                                ? `${locationName} (-)`
                                : `${locationName} (${formatQuantity(totalQuantity)})`,
                            value: locationId || "unknown",
                            quantity: totalQuantity,
                        };
                    })
                    .sort((a, b) => b.quantity - a.quantity); // Sort by quantity descending

                return options;
            };

            const inventoryOptions = getInventoryOptions(product.inventoryLevels);
            const firstOption = inventoryOptions[0]; // Now this will be the location with highest qty

            data[product.id] = {
                options: inventoryOptions,
                firstOption,
                displayText: firstOption?.label || t("productSearchModalAll.values.noStock"),
            };
        });

        return data;
    }, [modalProducts, locations, selectedLocationIds, t]);

    const handleCancel = () => {
        reset();
        setModalProducts(null);
        setModalSelectedProducts([]);
        onClose();
    };

    const isProductSelected = (product: any): boolean => {
        return modalSelectedProducts?.some((p: any) => {
            return p.productId === product.productId;
        });
    };

    const isAllSelected = useMemo(() => {
        if (!modalProducts || modalProducts.length === 0) return false;
        return modalProducts.every((p: any) => isProductSelected(p));
    }, [modalProducts, modalSelectedProducts]); // eslint-disable-line react-hooks/exhaustive-deps

    const isIndeterminate = useMemo(() => {
        if (!modalProducts || modalProducts.length === 0) return false;
        const selectedCount = modalProducts.filter((p: any) => isProductSelected(p)).length;
        return selectedCount > 0 && selectedCount < modalProducts.length;
    }, [modalProducts, modalSelectedProducts]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSelectAll = () => {
        if (isAllSelected) {
            setModalSelectedProducts([]);
        } else {
            const existingIds = new Set(modalSelectedProducts.map((p: any) => p.productId));
            const toAdd = modalProducts
                .filter((p: any) => !existingIds.has(p.productId))
                .map((product: any) => {
                    const productVarients = product.variants || [];
                    return {
                        ...product,
                        isActive: true,
                        variants: productVarients.map((v: any) => ({ ...v, isActive: true })),
                    };
                });
            setModalSelectedProducts((prev: any[]) => [...prev, ...toAdd]);
        }
    };

    const handleProductSelection = (product: any) => {
        setModalSelectedProducts((prevSelectedProducts: any[]) => {
            // Check if the product is already in the selected products
            const isAlreadySelected = prevSelectedProducts.some(
                (selectedProduct) => selectedProduct.productId === product.id
            );

            // If not already selected, append it to the list
            if (!isAlreadySelected) {
                product.isActive = true;
                const productVarients = product.variants || [];
                if (productVarients.length > 0) {
                    productVarients.forEach((variant: any) => {
                        variant.isActive = true;
                    });
                }
                product.variants = productVarients;
                return [...prevSelectedProducts, product];
            }

            if (isAlreadySelected) {
                return prevSelectedProducts.filter((selectedProduct) => selectedProduct.productId !== product.id);
            }

            // Otherwise, return the existing list
            return prevSelectedProducts;
        });
    };

    return (
        <div className="min-h-[75vh] max-h-[85vh] flex flex-col">
            {/* inner content */}
            <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-4 mb-6">
                    {/* First Row */}
                    <div className="grid grid-cols-3 gap-4">
                        {/* Keyword Search */}
                        <div>
                            <Controller
                                name="keywordSearch"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        label={t("productSearchModalAll.fields.searchProducts")}
                                        type="text"
                                        className="!my-0"
                                        inputClassName={`${field.value ? "pr-[30px]" : "pr-[30px] text-[12px]"}`}
                                        placeholder={t("productSearchModalAll.placeholders.searchKeyword")}
                                        error={errors.keywordSearch?.message}
                                    >
                                        <span className="absolute right-2 top-1/2 transform -translate-y-1/2 pt-2">
                                            <Icon name="search" className="opacity-70" />
                                        </span>
                                    </Input>
                                )}
                            />
                        </div>

                        {/* Product Category */}
                        {!hideCategory && <div className="cursor-not-allowed">
                            <CommonDropdown
                                control={control}
                                setValue={setValue}
                                label={t("productSearchModalAll.fields.productCategory")}
                                name="category"
                                type="categorytreebind"
                                placeholder={t("productSearchModalAll.placeholders.selectCategory")}
                                className="!my-0"
                                isMulti={true}
                                disabled={disableCategory}
                            />
                        </div>}
                        {/* Status */}
                        <div>
                            <CommonDropdown
                                preSelectValues={lookup
                                    .getProductStatus()
                                    .filter((s: any) => s.lookupId === LOOKUP_VALUE.PRODUCT_STATUS_ACTIVE)
                                    .map((s: any) => s.lookupId)}
                                control={control}
                                setValue={setValue}
                                label={t("productSearchModalAll.fields.status")}
                                name="status"
                                type="lookup"
                                placeholder={t("productSearchModalAll.placeholders.selectStatus")}
                                className="!my-0"
                                data={lookup.getProductStatus()}
                                isMulti={true}
                            />
                        </div>
                    </div>

                    {/* Second Row */}
                    <div className="grid grid-cols-4 gap-4">
                        {/* Location */}
                        <div>
                            <CommonDropdown
                                control={control}
                                setValue={setValue}
                                label={t("productSearchModalAll.fields.location")}
                                name="location"
                                type="allLocations"
                                placeholder={t("productSearchModalAll.placeholders.selectLocation")}
                                className="!my-0"
                                disabled={!!location && location.length > 0}
                                isMulti={true}
                            />
                        </div>
                        {/* Inventory */}
                        <div>
                            <CommonDropdown
                                control={control}
                                setValue={setValue}
                                label={t("productSearchModalAll.fields.inventory")}
                                name="inventory"
                                type="lookup"
                                placeholder={t("productSearchModalAll.placeholders.selectInventory")}
                                className="!my-0"
                                isMulti={true}
                                data={lookup.getInventoryStatus()}
                            />
                        </div>
                        {/* Third Row - Stock Quantity */}
                        <div className="grid grid-cols-1 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t("productSearchModalAll.fields.stockQuantity")}</label>
                                <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-300 p-1 py-[6px]">
                                    <div className="w-2/3">
                                        <Controller
                                            name="stockCondition"
                                            control={control}
                                            render={({ field: { value, onChange } }) => (
                                                <SelectBox
                                                    value={value as any}
                                                    onChange={onChange}
                                                    className="!my-0"
                                                    topParentStyle={{ marginBottom: 0 }}
                                                    size="sm"
                                                    placeholder={t("productSearchModalAll.placeholders.selectCondition")}
                                                    options={localizedConditionOptions}
                                                    error={errors.stockCondition?.message as string}
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="w-1/3">
                                        <Controller
                                            name="stockQty"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    {...field}
                                                    {...getPositiveWholeNumberInputProps()}
                                                    onChange={createPositiveWholeNumberHandler(field)}
                                                    className="!my-0"
                                                    size="sm"
                                                    placeholder={t("productSearchModalAll.placeholders.enterValue")}
                                                    label=""
                                                    error={errors.stockQty?.message}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Product Type */}
                        <div>
                            <CommonDropdown
                                control={control}
                                setValue={setValue}
                                name="productType"
                                label={t("productSearchModalAll.fields.productType")}
                                placeholder={t("productSearchModalAll.placeholders.selectProductType")}
                                className="!my-0"
                                type="lookup"
                                data={lookup.getProductType()}
                                isMulti={true}
                            />
                        </div>
                    </div>
                    {/* Search Button */}
                    <div className="flex justify-end">
                        <Button
                            type="button"
                            onClick={() => {
                                // Get all active statuses for reset
                                const statusLookup = lookup.getProductStatus();
                                const activeStatuses = statusLookup
                                    ?.filter((s: any) => s.lookupId === LOOKUP_VALUE.PRODUCT_STATUS_ACTIVE)
                                    .map((s: any) => ({ value: s.lookupId.toString(), label: getLookupDisplayValue(s, t) })) || [];

                                reset({
                                    keywordSearch: "",
                                    category: category || "",
                                    status: activeStatuses,
                                    inventory: "",
                                    stockCondition: "",
                                    stockQty: "",
                                    productType: "",
                                    location: location || "",
                                });

                                const resetSearchData = {
                                    ...defaultSearchValues,
                                    status: activeStatuses,
                                    location: location || ""
                                };
                                
                                setActiveSearchData(resetSearchData);
                                fetchAllProducts(resetSearchData);
                            }}
                            className="mr-2"
                        >
                            {t("productSearchModalAll.buttons.reset")}
                        </Button>
                        <Button type="button" onClick={handleSearchClick}>
                            {t("productSearchModalAll.buttons.search")}
                        </Button>
                    </div>
                </div>

                {/* Results Table */}
                <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-medium">{t("productSearchModalAll.values.productsFound", { count: totalRows || 0 })}</h3>
                    </div>

                    <div className="relative">
                        <InfiniteScrollTable
                            scrollId="discounts-product-search-scroll"
                            data={modalProducts}
                            hasMore={false}
                            onLoadMore={() => {}}
                            height={`${Math.max(400, Math.floor(window.innerHeight * 0.42))}px`}
                            enableSorting={false}
                            columns={[
                                {
                                    key: "select",
                                    label: (
                                        <input
                                            type="checkbox"
                                            checked={isAllSelected}
                                            ref={(el) => { if (el) el.indeterminate = isIndeterminate; }}
                                            onChange={handleSelectAll}
                                            className="rounded h-4 w-4 cursor-pointer"
                                            title={t("productSearchModalAll.values.selectAll")}
                                        />
                                    ),
                                    sortable: false,
                                    render: (product: any) => (
                                        <div className="flex items-center" style={{ marginLeft: product.level * 20 }}>
                                            <input
                                                type="checkbox"
                                                checked={isProductSelected(product)}
                                                onChange={(e) =>
                                                    // console.log("Select product", product.id)
                                                    handleProductSelection(product)
                                                }
                                                className="rounded h-4 w-4 cursor-pointer"
                                            />
                                        </div>
                                    ),
                                },
                                {
                                    key: "productName",
                                    label: t("productSearchModalAll.columns.productName"),
                                    sortable: false,
                                    render: (product: any) => (
                                        <div className="flex items-center gap-2">
                                            <div className="">
                                                <div className="font-medium break-words">{product.productName}</div>
                                                <div className="border-b-2 border-black my-1"></div>
                                                <div className="text-sm text-gray-500">
                                                    {(() => {
                                                        const productType = lookup.getProductType()?.find(
                                                            (type: any) => btoa(type?.lookupId) == product?.fkProductTypeLookupValueId
                                                        );
                                                        return productType ? getLookupDisplayValue(productType, t) + " " : "";
                                                    })()}{t("productSearchModalAll.values.product")} {product.variants?.length > 0
                                                        ? t("productSearchModalAll.values.variantsSuffix", { count: product.variants.length, formattedCount: formatQuantity(product.variants.length) })
                                                        : ""}
                                                </div>
                                            </div>
                                        </div>
                                    ),
                                },
                                { key: "categoryName", label: t("productSearchModalAll.columns.category"), sortable: false },
                                {
                                    key: "inventoryLevels",
                                    sortable: false,
                                    label: t("productSearchModalAll.columns.locationStock"),
                                    render: (product: any) => {
                                        const inventoryInfo = inventoryData[product.id];

                                        if (!inventoryInfo || inventoryInfo.options.length === 0) {
                                            return <span className="text-sm text-gray-500">{t("productSearchModalAll.values.noStockData")}</span>;
                                        }

                                        const { options, displayText } = inventoryInfo;

                                        return (
                                          <div className="min-w-[150px]">
                                            <SelectBox
                                              value={options[0] || null}
                                              onChange={() => {}}
                                              options={options}
                                            //   onDocument
                                              placeholder={displayText}
                                              className="!mb-0"
                                              size="sm"
                                              isClearable={false}
                                              menuPlacement="bottom"
                                              menuShouldScrollIntoView={false}
                                              customStyleOption={{
                                                menuList: (base: any) => ({
                                                  ...base,
                                                  maxHeight: "150px",
                                                }),
                                                menu: (base: any) => ({
                                                  ...base,
                                                  zIndex: 2,
                                                }),
                                              }}
                                            />
                                          </div>
                                        );
                                    },
                                },
                                {
                                    key: "vendors",
                                    label: t("productSearchModalAll.columns.costPrice"),
                                    align: "right" as const,
                                    sortable: false,
                                    // render: (product: any) => <div className="flex justify-end font-semibold text-right">{getRetailPrices(product.vendors, "costPerUnit")}</div>,
                                    render: (product: any) => <div className="flex justify-end font-semibold text-right">{getRetailPrices(product.prices, "costPricePerUnit")}</div>,
                                },
                                {
                                    key: "prices",
                                    sortable: false,
                                    label: getPricingMode(cashDiscountConfig) === 'dual-pricing' ? t("productSearchModalAll.columns.cardPrice") : t("productSearchModalAll.columns.retailPrice"),
                                    align: "right" as const,
                                    render: (product: any) => (
                                        <div className="flex justify-end font-semibold text-right">{getRetailPrices(product.prices, "retailPriceExclTax")}</div>
                                    ),
                                },
                                ...(getPricingMode(cashDiscountConfig) !== 'retail-only' ? [{
                                    key: "cashPrices",
                                    sortable: false,
                                    label: t("productSearchModalAll.columns.cashPrice"),
                                    align: "right" as const,
                                    render: (product: any) => (
                                        <div className="flex justify-end font-semibold text-right">
                                            {getCashPriceDisplay(product.prices, cashDiscountConfig)}
                                        </div>
                                    ),
                                }] : []),
                                ]}
                            />
                    </div>
                </div>
            </div>
            {/* Footer Buttons */}
            <div className="z-50 flex justify-end gap-3 pt-4 border-t px-6 pb-6">
                <Button type="button" onClick={handleCancel} theme="secondary">
                    {t("productSearchModalAll.buttons.cancel")}
                </Button>
                <Button
                    type="button"
                    onClick={() => {
                        // For each selected product, prefer the fresh search result data
                        // over the stale pre-selected data (productData prop)
                        const refreshed = modalSelectedProducts.map((selected: any) => {
                            const fresh = modalProducts?.find(
                                (p: any) => p.productId === selected.productId
                            );
                            return fresh ? { ...fresh, isActive: selected.isActive, variants: selected.variants } : selected;
                        });
                        onConfirm(refreshed);
                    }}
                >
                    {t("productSearchModalAll.buttons.confirmSelection")}
                </Button>
            </div>
        </div>
    );
}

export default ProductSearchModalAll;
